"use client";

import React from "react";
import Image from 'next/image';
import { Globe } from "lucide-react";
import { BrandSVGIcons } from "./CommonComponents/IconsComponent";
import { getBrandNameFromHostname, isSpanishURL } from "@/lib/utils/brandUtils";
import { BRAND_SECTIONS } from "@/lib/constants/appConstants";

interface LogosRowProps {
  brandItems: (typeof BRAND_SECTIONS)[number][];
  brandName: string;
  brandIconMap: Record<string, () => React.ReactNode>;
  onNavigate: (dest?: string) => void;
}

const LogosRow: React.FC<LogosRowProps> = ({ brandItems, brandName, brandIconMap, onNavigate }) => (
  <div className="flex items-center gap-2 sm:gap-4 md:gap-5 whitespace-nowrap">
    {brandItems.map((brand) => {
      if(brand.id.includes(brandName)) {
        const SvgIcon = brandIconMap[brand.id];
        const imageContent = brand.image?.src ? (
          <Image
            src={brand.image.src}
            alt={brand.image.alt || 'Brand logo'}
            width={120}
            height={18}
            className="h-[14px] sm:h-[16px] md:h-[18px] w-auto pointer-events-none"
          />
        ) : null;
        const iconContent = SvgIcon ? (
          <div className="w-auto flex items-center pointer-events-none [&>svg]:h-[40px] [&>svg]:w-auto sm:[&>svg]:h-[50px] md:[&>svg]:h-[60px]">{SvgIcon()}</div>
        ) : imageContent;
        return (
          <button
            key={brand.id}
            type="button"
            onClick={() => onNavigate(brand.cta?.destination)}
            className="opacity-90 hover:opacity-100 transition-opacity flex items-center cursor-pointer"
            aria-label={brand.image?.alt || brand.id}
          >
            {iconContent}
          </button>
        );
      }
    })}
  </div>
);

// Global Header Component Renderer
const GlobalHeaderRenderer: React.FC = () => {
    const brandName = getBrandNameFromHostname();
    const brandItems = BRAND_SECTIONS.filter((item) => item.id?.startsWith('brand_')) || [];
    const languageItems = BRAND_SECTIONS.filter((item) => item.id?.startsWith('language_')) || [];
    const brandIconMap: Record<string, () => React.ReactNode> = {
        brand_safelink: BrandSVGIcons.safelink,
        brand_simplemobile: BrandSVGIcons.simplemobile,
        brand_tracfone: BrandSVGIcons.tracfone,
        brand_totalwireless: BrandSVGIcons.totalWireless,
        brand_net10: BrandSVGIcons.net10Wireless,
        brand_gosmartmobile: BrandSVGIcons.gosmartmobile,
        brand_straighttalk: BrandSVGIcons.straighttalk,
        brand_familymobile: BrandSVGIcons.familymobile,
        brand_clearway: BrandSVGIcons.clearway,
    };
    const isSpanish = isSpanishURL(); // Determine if the current URL is Spanish

    const handleNavigate = (dest?: string) => {
    if (dest) {
      // Toggle between Spanish and English URLs
      if (dest === 'language_english') {
        // Convert Spanish URL to English
        const baseDest = globalThis.location.href.replace('/es/', '/');
        globalThis.location.href = baseDest;
        return;
      } else if (dest === 'language_spanish') {
        // Convert English URL to Spanish
        const spanishDest = globalThis.location.href.replace('/device/device-unlock', '/device/es/device-unlock');
        globalThis.location.href = spanishDest;
        return;
      } else if (dest.startsWith('http://') || dest.startsWith('https://')) {
        // Check if it's an external URL
        window.open(dest, '_blank', 'noopener,noreferrer');
      }
    }
  };
    return (
      <header className="w-full h-[80px]">
        <div className="h-full w-full px-4 relative">
          {/* ✅ Desktop: centered logos */}
          <div className="h-full relative">
            <div className="absolute inset-0 flex items-center justify-start pointer-events-none z-20 pl-2 sm:pl-4 md:pl-12 pr-2">
              <div className="pointer-events-auto">
                <LogosRow brandItems={brandItems} brandName={brandName} brandIconMap={brandIconMap} onNavigate={handleNavigate} />
              </div>
            </div>

            {/* Right controls (desktop & mobile) */}
            <div className="h-full flex items-center justify-end gap-4 relative z-30">
              {languageItems.map((language) => {
          if(isSpanish && language.id === 'language_spanish') return null;
          if(!isSpanish && language.id === 'language_english') return null;
          const languageText = language.id.toLowerCase().includes('spanish') ? 'Español' : 'English';
          return (
            <button
              key={language.id}
              aria-label={languageText === 'Español' ? 'Sitio en español' : 'English site'}
              className="top-menu-item text-xs font-medium text-[#000] hover:opacity-90 transition-opacity flex items-center"
              id="language"
              tabIndex={0}
              onClick={() => handleNavigate(language.id)}
            >
              <Globe className="h-4 w-4 mr-2"/>
              {languageText}
            </button>
          );
              })}
            </div>
          </div>

          {/* ✅ Mobile: ONLY hamburger (no logos) */}
          {/* <div className="md:hidden h-full flex items-center justify-end">
            <button
              type="button"
              className="text-white opacity-90 hover:opacity-100 transition-opacity"
              aria-label="Open menu"
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none">
                <path
                  d="M4 7h16M4 12h16M4 17h16"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div> */}
        </div>
      </header>
    );
};

export default GlobalHeaderRenderer;